import { calcRulesFor } from './rulesVersion';
import { Equipment, Project } from '../../types/project';
import { serviceCapacityKVA } from '../serviceConfig';
import { defaultEditionForState, getEditionPack } from './editions';
import { checkTraditionalEligibility } from './eligibility';
import { existingLoads, enteredProposedLoads } from './loadRoles';
import { calculateExistingDwelling } from './methods/existing22083';
import { TraditionalCalcResults, TraditionalSubmethod } from './types';

// Bridges the stored Project shape to the pure traditional engine, the same
// way ResultsView bridges to calculateNEC22087. Keeps every mapping decision
// (edition fallback, role filtering, submethod override) in one tested place.

/** The minimum input for a traditional result to be computable at all —
 * completion validation demands more (attestation, rated rows). */
export function hasTraditionalInputs(project: Partial<Project>): boolean {
  return (
    typeof project.dwelling_floor_area_sqft === 'number' &&
    project.dwelling_floor_area_sqft > 0
  );
}

/** The one question every consumer should ask before calling the runner:
 * inputs present AND the project is inside the section's scope. */
export function canRunTraditional(project: Partial<Project>): boolean {
  return hasTraditionalInputs(project) && checkTraditionalEligibility(project).eligible;
}

export function runTraditionalCalc(
  project: Partial<Project>,
  equipment: Equipment[]
): TraditionalCalcResults {
  // Defence in depth: no code path may produce a traditional result for a
  // project outside the section's scope — the UI and validation gate first,
  // and this throw catches anything they miss.
  const gate = checkTraditionalEligibility(project);
  if (!gate.eligible) {
    throw new Error(
      `Traditional calculation is not applicable to this project (${gate.reason}). ${gate.message}`
    );
  }
  const pack = getEditionPack(
    project.code_edition ?? defaultEditionForState(project.customer_state)
  );

  const override = project.traditional_submethod;
  const submethodOverride: TraditionalSubmethod | undefined =
    override === '220.83A' || override === '220.83B' || override === '120.83'
      ? override
      : undefined;

  return calculateExistingDwelling({
    profile: {
      floorAreaSqFt: project.dwelling_floor_area_sqft ?? 0,
      smallApplianceCircuits: project.small_appliance_circuit_count ?? 2,
      laundryCircuits: project.laundry_circuit_count ?? 1,
      addedFloorAreaSqFt: project.added_floor_area_sqft ?? 0,
    },
    existingLoads: existingLoads(equipment),
    proposedLoads: enteredProposedLoads(equipment),
    serviceCapacityKVA: serviceCapacityKVA(
      project.service_rating_amps ?? 0,
      project.service_voltage_numeric ?? 240,
      project.service_phase === 'three' ? 'three' : 'single'
    ),
    pack,
    submethodOverride,
    noncoincidentElectionUnderA: project.noncoincident_election ?? false,
    applyEvseFloor: project.apply_evse_floor ?? true,
    calcRules: calcRulesFor(project),
    // No `overrides` and no `evsePolicy` — removed from the product 2026-09-06.
    //
    // The Results tab used to expose the first tier, tier %, remainder % and
    // EVSE treatment as editable fields. Typing 10 where the section says 40
    // turned a service that genuinely failed by 25 A into a PASS, and across 52
    // production projects not one preparer had ever changed a value. An AHJ
    // directing every contractor to depart from the Code's own numbers is not a
    // real scenario, so the capability was liability without a use.
    //
    // The engine still implements both, with tests, and the `override_*` and
    // `evse_demand_policy` columns still exist and still hold their (entirely
    // null) data. Nothing reaches them from here, which is what makes the
    // calculation always the section's own. Reactivating means restoring a UI
    // and passing the values through again. See load-calc-program.md §7.
  });
}
